import React, { useState, useEffect } from 'react';
import { questionBankService } from '../services/questionBankService';
import { QuestionBank } from '../types';
import { PlusIcon } from './Icons';

interface SaveToBankModalProps {
  isOpen: boolean;
  questionnaireId: number;
  questionId: number;
  onClose: () => void;
  onSaved: () => void;
}

const SaveToBankModal: React.FC<SaveToBankModalProps> = ({ isOpen, questionnaireId, questionId, onClose, onSaved }) => {
  const [banks, setBanks] = useState<QuestionBank[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newBankName, setNewBankName] = useState('');
  const [newBankDesc, setNewBankDesc] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchBanks();
      setSelectedBankId(null);
      setShowCreate(false);
      setNewBankName('');
      setNewBankDesc('');
    }
  }, [isOpen]);

  const fetchBanks = async () => {
    try {
      setLoading(true);
      const data = await questionBankService.getAllBanks();
      setBanks(data);
    } catch (error) {
      console.error('Failed to fetch banks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBank = async () => {
    if (!newBankName.trim()) return;
    try {
      const newBank = await questionBankService.createBank({ name: newBankName, description: newBankDesc || undefined });
      setBanks(prev => [newBank, ...prev]);
      setSelectedBankId(newBank.id);
      setShowCreate(false);
      setNewBankName('');
      setNewBankDesc('');
    } catch (error) {
      console.error('Failed to create bank:', error);
    }
  };

  const handleSave = async () => {
    if (!selectedBankId) return;
    try {
      setSaving(true);
      await questionBankService.saveQuestionToBank(questionnaireId, questionId, selectedBankId);
      onSaved();
    } catch (error) {
      console.error('Failed to save to bank:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[80vh] flex flex-col">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">存入题库</h3>

        {loading ? (
          <div className="text-center py-8 text-gray-500">加载中...</div>
        ) : (
          <div className="flex-1 overflow-y-auto mb-4">
            {/* Quick create */}
            {!showCreate ? (
              <button
                onClick={() => setShowCreate(true)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg border border-dashed border-primary-300 mb-3"
              >
                <PlusIcon size={16} />
                新建题库
              </button>
            ) : (
              <div className="mb-3 p-3 border border-primary-200 rounded-lg bg-primary-50">
                <input
                  type="text"
                  value={newBankName}
                  onChange={(e) => setNewBankName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
                  placeholder="题库名称"
                  autoFocus
                />
                <input
                  type="text"
                  value={newBankDesc}
                  onChange={(e) => setNewBankDesc(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
                  placeholder="描述（可选）"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCreate(false)}
                    className="px-3 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleCreateBank}
                    disabled={!newBankName.trim()}
                    className="px-3 py-1 text-xs text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50"
                  >
                    创建
                  </button>
                </div>
              </div>
            )}

            {/* Bank list */}
            {banks.length === 0 ? (
              <div className="text-center py-6 text-sm text-gray-500">
                暂无题库，请先创建一个题库
              </div>
            ) : (
              <div className="space-y-2">
                {banks.map((bank) => (
                  <button
                    key={bank.id}
                    onClick={() => setSelectedBankId(bank.id)}
                    className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                      selectedBankId === bank.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-sm text-gray-900">{bank.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{bank.questionCount} 道题目</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedBankId || saving}
            className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? '保存中...' : '存入题库'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveToBankModal;
